import SchedulingVessel from "@/components/toto/SchedulingVessel";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import {
  ArrowLeft, CheckCircle2, ClipboardList, Info, Eye, User as UserIcon,
} from "lucide-react";

/**
 * Zenoti Update Queue — the operational bridge.
 *
 * Every approved schedule change surfaces here as a task. Two beats:
 *   pending  → updated (someone reflected the change in Zenoti)
 *            → verified (a second pair of eyes confirmed it)
 *
 * The verifier CANNOT be the same person who marked it updated — this is
 * where a second pair of eyes catches a mistake that would otherwise flow
 * silently into guest bookings and payroll.
 */

const STATUS = {
  pending:  { dot: "bg-amber-500",   label: "Pending Zenoti update" },
  updated:  { dot: "bg-indigo-500",  label: "Updated · awaiting verification" },
  verified: { dot: "bg-emerald-500", label: "Verified" },
};

const RELATED_LABEL = {
  eg_amendment: "EG shift amendment",
  time_off: "Time-off request",
  swap: "Shift swap",
  coverage: "Coverage assignment",
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] schedule-muted bg-stone-50 px-2.5 py-0.5 rounded-full" data-testid={`zenoti-status-${status}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function TaskCard({ task, currentUserId, onUpdated, onVerified }) {
  const [markNote, setMarkNote] = useState("");
  const [verifyNote, setVerifyNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const cannotVerify = task.status === "updated" && task.updated_in_zenoti_by === currentUserId;

  const markUpdated = async () => {
    setBusy(true); setErr("");
    try { await onUpdated(task.id, markNote); setMarkNote(""); }
    catch (e) { setErr(e.response?.data?.detail || "Could not update."); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setErr("");
    try { await onVerified(task.id, verifyNote); setVerifyNote(""); }
    catch (e) { setErr(e.response?.data?.detail || "Could not verify."); }
    finally { setBusy(false); }
  };

  return (
    <div className="schedule-panel schedule-border rounded-3xl p-5 " data-testid={`zenoti-task-${task.id}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] label-eyebrow schedule-muted">{RELATED_LABEL[task.related_type] || task.related_type}</div>
          <div className="schedule-ink mt-1">{task.summary}</div>
          {task.reason && <div className="text-xs schedule-muted mt-1">Reason: {task.reason}</div>}
        </div>
        <StatusPill status={task.status} />
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px] schedule-muted">
        <div>
          <div className="label-eyebrow schedule-muted mb-0.5">Created</div>
          <div className="schedule-ink">{task.created_by_name} · {new Date(task.created_at).toLocaleString()}</div>
        </div>
        {task.updated_in_zenoti_at && (
          <div data-testid={`zenoti-task-${task.id}-updated-info`}>
            <div className="label-eyebrow schedule-muted mb-0.5">Updated in Zenoti</div>
            <div className="schedule-ink">{task.updated_in_zenoti_by_name} · {new Date(task.updated_in_zenoti_at).toLocaleString()}</div>
            {task.updated_note && <div className="text-[11px] schedule-muted mt-0.5">"{task.updated_note}"</div>}
          </div>
        )}
        {task.verified_at && (
          <div data-testid={`zenoti-task-${task.id}-verified-info`}>
            <div className="label-eyebrow schedule-muted mb-0.5">Verified</div>
            <div className="schedule-ink">{task.verified_by_name} · {new Date(task.verified_at).toLocaleString()}</div>
            {task.verified_note && <div className="text-[11px] schedule-muted mt-0.5">"{task.verified_note}"</div>}
          </div>
        )}
      </div>

      {task.status === "pending" && (
        <div className="mt-4 flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <input value={markNote} onChange={(e) => setMarkNote(e.target.value)} placeholder="Note (optional) — what did you update?"
              className="w-full text-sm px-3 py-1.5 rounded-lg schedule-border outline-none" data-testid={`zenoti-updated-note-${task.id}`} />
          </div>
          <button onClick={markUpdated} disabled={busy} data-testid={`zenoti-mark-updated-${task.id}`}
            className="text-sm px-4 py-1.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-2">
            <ClipboardList size={14} strokeWidth={1.5} /> Mark updated in Zenoti
          </button>
        </div>
      )}

      {task.status === "updated" && (
        <div className="mt-4 flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <input value={verifyNote} onChange={(e) => setVerifyNote(e.target.value)} placeholder="Verification note (optional)"
              className="w-full text-sm px-3 py-1.5 rounded-lg schedule-border outline-none" data-testid={`zenoti-verify-note-${task.id}`} />
          </div>
          {cannotVerify ? (
            <div className="text-xs schedule-muted flex items-center gap-1.5 bg-stone-50 px-3 py-1.5 rounded-full" data-testid={`zenoti-verify-blocked-${task.id}`}>
              <Info size={12} strokeWidth={1.5} /> Ask a teammate to verify — a second pair of eyes.
            </div>
          ) : (
            <button onClick={verify} disabled={busy} data-testid={`zenoti-verify-${task.id}`}
              className="text-sm px-4 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-2">
              <CheckCircle2 size={14} strokeWidth={1.5} /> Verify
            </button>
          )}
        </div>
      )}

      {err && <div className="mt-3 text-sm text-rose-600" data-testid={`zenoti-task-error-${task.id}`}>{err}</div>}
    </div>
  );
}

export default function ZenotiQueue() {
  const { user } = useAuth();
  const [boutique, setBoutique] = useState(user?.boutique_code || "PLT");
  const [status, setStatus] = useState("pending");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const isLeadership = user && !user.boutique_code;
  const boutiques = ["PLT", "FLL", "WYN", "PNC"];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/scheduling/zenoti-tasks", { params: { boutique, status: status === "all" ? undefined : status } });
      setTasks(r.data?.tasks || []);
    } finally { setLoading(false); }
  }, [boutique, status]);

  useEffect(() => { load(); }, [load]);

  const markUpdated = async (id, note) => {
    await api.put(`/scheduling/zenoti-tasks/${id}/updated`, { note });
    // Keep the just-updated card in view — flip filter so the verify chip shows.
    if (status === "pending") setStatus("updated");
    else await load();
  };
  const verify = async (id, note) => {
    await api.put(`/scheduling/zenoti-tasks/${id}/verify`, { note });
    await load();
  };

  const filterCounts = tasks; // display current filter

  return (
    <SchedulingVessel title="Zenoti updates" description="Update approved changes in Zenoti, then have a teammate verify them.">
        <div className="flex items-baseline justify-end flex-wrap gap-3 mt-2">
          {isLeadership && (
            <select value={boutique} onChange={(e) => setBoutique(e.target.value)} data-testid="zenoti-boutique"
              className="px-4 py-2 rounded-full schedule-border text-sm schedule-panel outline-none">
              {boutiques.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
        </div>

        <div className="mt-8 flex items-center gap-1" data-testid="zenoti-filter">
          {[
            { id: "pending", label: "Pending" },
            { id: "updated", label: "Awaiting verification" },
            { id: "verified", label: "Verified" },
            { id: "all", label: "All" },
          ].map((t) => (
            <button key={t.id} aria-pressed={status === t.id} onClick={() => setStatus(t.id)} data-testid={`zenoti-filter-${t.id}`}
              className={`text-xs px-3 py-1.5 rounded-full ${status === t.id ? "bg-stone-900 text-white" : "schedule-panel schedule-border schedule-muted hover:bg-stone-50"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="text-sm schedule-muted">Loading…</div>
          ) : filterCounts.length === 0 ? (
            <div className="schedule-panel schedule-border rounded-3xl p-10 text-sm schedule-muted text-center" data-testid="zenoti-empty">
              Nothing in this bucket. The queue is calm — a good sign.
            </div>
          ) : filterCounts.map((t) => (
            <TaskCard key={t.id} task={t} currentUserId={user?.user_id} onUpdated={markUpdated} onVerified={verify} />
          ))}
        </div>
    </SchedulingVessel>
  );
}
